import type {
  HotelAvailabilityTrackingProperties,
  HotelRoomRateTrackingProperties,
  HotelRoomTrackingProperties,
  HotelShopAvailabilityTrackingProperties,
  HotelShopTrackingProperties,
  HotelViewedLodgingInListTrackingProperties,
} from "@b2bportal/core-tracking";
import type { LodgingCancelScenarioEnum } from "@b2bportal/lodging-cancel-api/lib/api";
export enum HotelCommonTrackingEventsName {
  // Hotels app loaded
  hotel_entry = "hotel_entry",
  modal_alert = "modal_alert",
}

export enum LodgingShopTrackingEvents {
  // Not present in the docs
  hotel_tapped_enter_booking_flow = "hotel_tapped_enter_booking_flow",
  // When rooms are loaded
  hotel_loaded_room_info_list = "hotel_loaded_room_info_list",
  // When details are opened
  hotel_tapped_bed_types = "hotel_tapped_bed_types",
  // when room details are loaded
  hotel_viewed_bed_types = "hotel_viewed_bed_types",

  hotel_tapped_cancellation_policy = "hotel_tapped_cancellation_policy",
  hotel_view_cancellation_policy = "hotel_view_cancellation_policy",

  hotel_viewed_room_in_list = "hotel_viewed_room_in_list",

  // When continue button is tapped in room selection
  hotel_tapped_room_selection_continue = "hotel_tapped_room_selection_continue",

  // when we display results in the availability page for the first time. Not repeatable
  hotel_loaded_list_page = "hotel_loaded_list_page",

  // when the calendar is opened
  hotel_viewed_calendar = "hotel_viewed_calendar",
  // when the map is loaded
  hotel_viewed_map = "hotel_viewed_map",
  // when the list is viewed
  hotel_viewed_list = "hotel_viewed_list",

  // When search page is loaded
  hotel_viewed_search = "hotel_viewed_search",

  // When filter is tapped
  hotel_tapped_filter = "hotel_tapped_filter",

  // Only in commbank when the filters are applied in the modal (button is pressed)
  hotel_applied_filters = "hotel_applied_filters",

  // When all filters dialog opens => only in commbank
  hotel_viewed_filters = "hotel_viewed_filters",

  // When the sort button is tapped
  hotel_tapped_sort = "hotel_tapped_sort",

  // When a sort option is tapped => Not used
  hotel_applied_sort = "hotel_applied_sort",

  // When the "redo search in map" button is tapped
  hotel_tapped_redo_search_in_map = "hotel_tapped_redo_search_in_map",

  // When a result is viewed. Not repeatable for the same search
  hotel_viewed_lodging_in_list = "hotel_viewed_lodging_in_list",

  // When a result is tapped. Not repeatable for the same search
  hotel_tapped_lodging_in_list = "hotel_tapped_lodging_in_list",

  // When a marker is tapped. Only in commbank
  hotel_tapped_map_marker = "hotel_tapped_map_marker",

  // On click hotel navigation
  hotel_swiped_carousel = "hotel_swiped_carousel",

  // Shop page loaded details correctly
  hotel_viewed_cover = "hotel_viewed_cover",

  // When any modal opens. This event is too generic. We should specify more granular ones for specific cases
  modal_alert = "modal_alert",

  // Cfar page is viewed => Trip advisor only
  hotel_viewed_add_on = "hotel_viewed_add_on",

  // User clicks on go to next step on cfar => Trip advisor only
  hotel_cfar_add_on_choice = "hotel_cfar_add_on_choice",

  // on change sort
  hotel_updated_list_page = "hotel_updated_list_page",

  // When the guests are updated for some tenants and when the guest popup is opened for others
  hotel_tapped_guests = "hotel_tapped_guests",

  // When the guests are updated for the tenants that previously trigger hotel_tapped_guests when the popup opens
  hotel_tapped_apply_guests = "hotel_tapped_apply_guests",

  // When a user taps an option from the autocomplete
  tapped_search_suggestion = "tapped_search_suggestion",
  // Firing when you select an individual date on the calendar (will fire twice)
  hotel_selected_calendar_date = "hotel_selected_calendar_date",

  // Confirming the date change
  hotel_tapped_select_dates = "hotel_tapped_select_dates",

  // When the offer loads in any page
  viewed_offer = "viewed_offer",
  // When a user taps a card in the map view
  hotel_tapped_map_card = "hotel_tapped_map_card",
}

export enum LodgingSelfServeTrackingEvents {
  self_serve_cancel_response_success = "self_serve_cancel_response_success",
  self_serve_click_cancel_button = "self_serve_click_cancel_button",
  self_serve_viewed_cancel_modal = "self_serve_viewed_cancel_modal",
}

export const HOTEL_PRODUCT_TYPE = "hotel" as const;

export type HotelHTSEventBasicProperties = {
  productType: typeof HOTEL_PRODUCT_TYPE;
};

type EncryptedProperties = {
  encryptedProperties?: string[];
};

export type HotelHTSEventProperties<Properties> = HotelHTSEventBasicProperties &
  Properties;

export const HotelEventName = {
  ...HotelCommonTrackingEventsName,
  ...LodgingShopTrackingEvents,
  ...LodgingSelfServeTrackingEvents,
};
export type HotelEventName = typeof HotelEventName;

export type HotelEntry = {
  name: HotelCommonTrackingEventsName.hotel_entry;
  properties: HotelHTSEventProperties<{
    entry: string;
  }>;
} & EncryptedProperties;

export type HotelViewedSearch = {
  name: LodgingShopTrackingEvents.hotel_viewed_search;
  properties: Record<string, unknown>;
} & EncryptedProperties;

export type HotelRoomInfoListLoaded = {
  name: LodgingShopTrackingEvents.hotel_loaded_room_info_list;
  properties: HotelHTSEventProperties<
    HotelShopTrackingProperties["properties"] &
      HotelShopAvailabilityTrackingProperties["properties"]
  >;
} & EncryptedProperties;

export type HotelRoomDetailsOpened = {
  name: LodgingShopTrackingEvents.hotel_tapped_bed_types;
  properties: HotelHTSEventProperties<
    HotelShopTrackingProperties["properties"] &
      HotelShopAvailabilityTrackingProperties["properties"]
  >;
} & EncryptedProperties;

export type HotelCoverViewed = {
  name: LodgingShopTrackingEvents.hotel_viewed_cover;
  properties: HotelHTSEventProperties<
    HotelShopTrackingProperties["properties"] &
      HotelShopAvailabilityTrackingProperties["properties"]
  >;
} & EncryptedProperties;

export type HotelListLoaded = {
  name: HotelEventName["hotel_loaded_list_page"];
  properties: HotelHTSEventProperties<
    HotelAvailabilityTrackingProperties["properties"]
  >;
} & EncryptedProperties;

export type HotelUpdatedListPage = {
  name: HotelEventName["hotel_updated_list_page"];
  properties: HotelHTSEventProperties<
    HotelAvailabilityTrackingProperties["properties"]
  >;
} & EncryptedProperties;

export type HotelViewedList = {
  name: HotelEventName["hotel_viewed_list"];
  properties: HotelHTSEventProperties<
    HotelAvailabilityTrackingProperties["properties"]
  >;
};

export type HotelMapLoaded = {
  name: HotelEventName["hotel_viewed_map"];
  properties: HotelHTSEventProperties<
    HotelAvailabilityTrackingProperties["properties"]
  >;
} & EncryptedProperties;

export type HotelViewedLodgingInList = {
  name: LodgingShopTrackingEvents.hotel_viewed_lodging_in_list;
  properties: HotelHTSEventProperties<
    HotelAvailabilityTrackingProperties["properties"] &
      HotelViewedLodgingInListTrackingProperties["properties"]
  >;
} & EncryptedProperties;

export type HotelSwipedCarousel = {
  name: LodgingShopTrackingEvents.hotel_swiped_carousel;
  properties: HotelHTSEventProperties<
    HotelAvailabilityTrackingProperties["properties"] &
      HotelViewedLodgingInListTrackingProperties["properties"]
  >;
} & EncryptedProperties;

export type HotelRoomSelected = {
  name: HotelEventName["hotel_tapped_room_selection_continue"];
  properties: HotelHTSEventProperties<
    HotelShopAvailabilityTrackingProperties["properties"] &
      HotelShopTrackingProperties["properties"] &
      HotelRoomTrackingProperties["properties"]
  > &
    HotelRoomRateTrackingProperties["properties"];
} & EncryptedProperties;

export type HotelViewedRoomInList = {
  name: LodgingShopTrackingEvents.hotel_viewed_room_in_list;
  properties: HotelHTSEventProperties<
    HotelShopTrackingProperties["properties"] &
      HotelShopAvailabilityTrackingProperties["properties"]
  >;
} & EncryptedProperties;

export type HotelCancelReservationComponentViewed = {
  name: HotelEventName["self_serve_viewed_cancel_modal"];
  properties: {
    cancel_scenario: LodgingCancelScenarioEnum;
    non_cfar: boolean;
  };
} & EncryptedProperties;
export type HotelCancelReservationSuccessViewed = {
  name: HotelEventName["self_serve_cancel_response_success"];
  properties: {
    product: typeof HOTEL_PRODUCT_TYPE;
    cancel_scenario: LodgingCancelScenarioEnum;
    non_cfar: boolean;
  };
} & EncryptedProperties;

export type HotelConfirmCancelReservationButtonClicked = {
  name: HotelEventName["self_serve_click_cancel_button"];
  properties: {
    cancel_scenario: LodgingCancelScenarioEnum;
    non_cfar: boolean;
  };
} & EncryptedProperties;

export type HotelGuestsSelectionOpened = {
  name: LodgingShopTrackingEvents.hotel_tapped_guests;
  properties: {
    adults_count: number;
    children_count: number;
  };
} & EncryptedProperties;
export type HotelGuestsSelected = {
  name: LodgingShopTrackingEvents.hotel_tapped_apply_guests;
  properties: {
    adults_count: number;
    children_count: number;
  };
} & EncryptedProperties;
export type HotelCalendarOpened = {
  name: LodgingShopTrackingEvents.hotel_viewed_calendar;
  properties: Record<string, unknown>;
} & EncryptedProperties;
export type HotelCalendarDateChanged = {
  name: LodgingShopTrackingEvents.hotel_selected_calendar_date;
  properties: {
    advance: number;
    check_in_date: string;
    check_out_date?: string;
    is_last_minute: boolean;
    los: number;
  };
} & EncryptedProperties;
export type HotelCalendarDatesSelected = {
  name: LodgingShopTrackingEvents.hotel_tapped_select_dates;
  properties: {
    advance: number;
    check_in_date: string;
    check_out_date: string;
    is_last_minute: boolean;
    los: number;
  };
} & EncryptedProperties;

export const HOTEL_PRICE_INCREASED = "hotel_price_increase";
export const HOTEL_PRICE_DECREASE = "hotel_price_decrease";

export type HotelPriceChangedCheckoutModalOpened = {
  name: HotelEventName["modal_alert"];
  properties: {
    product_type: typeof HOTEL_PRODUCT_TYPE;
    modal_type: typeof HOTEL_PRICE_DECREASE | typeof HOTEL_PRICE_INCREASED;
    price_change: number;
    country_code: string;
    initial_price: number;
    updated_price: number;
  };
} & EncryptedProperties;
export type HotelFilterTapped = {
  name: HotelEventName["hotel_tapped_filter"];
  properties: {};
} & EncryptedProperties;

export type HotelMapCardTapped = {
  name: HotelEventName["hotel_tapped_map_card"];
  properties: {};
} & EncryptedProperties;

export type HotelTappedRedoSearchInMap = {
  name: LodgingShopTrackingEvents.hotel_tapped_redo_search_in_map;
  properties: {
    page_property_count?: number;
    page_index?: number;
  };
};

export type HotelHTSEvent =
  | HotelEntry
  | HotelViewedSearch
  | HotelRoomInfoListLoaded
  | HotelCoverViewed
  | HotelMapLoaded
  | HotelViewedList
  | HotelListLoaded
  | HotelRoomSelected
  | HotelCancelReservationSuccessViewed
  | HotelCancelReservationComponentViewed
  | HotelConfirmCancelReservationButtonClicked
  | HotelGuestsSelectionOpened
  | HotelGuestsSelected
  | HotelCalendarOpened
  | HotelCalendarDateChanged
  | HotelCalendarDatesSelected
  | HotelPriceChangedCheckoutModalOpened
  | HotelViewedLodgingInList
  | HotelSwipedCarousel
  | HotelUpdatedListPage
  | HotelRoomDetailsOpened
  | HotelViewedRoomInList
  | HotelMapCardTapped
  | HotelFilterTapped
  | HotelTappedRedoSearchInMap;

export type HotelShopListTrackingProperties = {
  indexInList: number;
};
